import { ComponentProps, ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ArenaTicket } from '@sorare/core/src/atoms/icons/rivals/branded/ArenaTicket';

import { BrandedPlayButtonStatus } from './types';

const TopHalf = styled.span`
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  height: 50%;
  width: 100%;
  transform-origin: bottom left;
  transform: translateX(calc(var(--offset-width) * -1));
  &::before,
  &::after {
    position: absolute;
    content: '';
    transition: 0.3s ease transform;
  }
  &::before {
    height: 100%;
    width: var(--border-width);
    left: 100%;
    background: var(--border-right-color);
    transform: skewY(45deg);
    transform-origin: bottom left;
  }
  &::after {
    top: 100%;
    height: var(--border-width);
    width: 100%;
    left: calc(var(--border-width) / 2);
    transform: skewX(45deg);
    background: var(--border-bottom-color);
  }
`;
const BottomHalf = styled(TopHalf)`
  transform: none;
  bottom: 0;
  top: auto;
`;

const Root = styled(ButtonBase)`
  /* Margins to offset the transforms on the button and make sure it does not overflow its container */
  margin: 6px var(--double-unit) 5px 6px;

  /** theme */
  --thin-top-border-color: rgba(255, 255, 255, 0.5);
  --border-bottom-color: linear-gradient(90deg, #14164d 0%, #003275 89.59%);
  --border-right-color: linear-gradient(to bottom, #013275 0%, #003275 89.59%);
  --text-shadow-color: #0a0084;
  --top-half-gradient-color: linear-gradient(
      295deg,
      #43b6fc 0,
      rgba(43, 139, 252, 0.1) 30%,
      rgba(41, 136, 252, 0)
    ),
    linear-gradient(90deg, #4348fc 0%, #2913fb 100%);
  --bottom-half-gradient-color: linear-gradient(
      295deg,
      #43b6fc 0,
      rgba(43, 139, 252, 0.1) 30%,
      rgba(41, 136, 252, 0)
    ),
    linear-gradient(90deg, #4348fc -7.63%, #2988fc 108.26%);

  /** settings */
  --border-width: var(--unit);
  --half-border-width: calc(var(--border-width) / 2);
  --offset-width: var(--unit);
  --skew: 25deg;

  position: relative;
  isolation: isolate;
  display: flex;
  justify-content: center;
  align-items: center;

  &.matchedUp {
    /** theme */
    --thin-top-border-color: rgba(255, 255, 255, 0.5);
    --border-bottom-color: linear-gradient(90deg, #6e012f 0%, #540128 89.59%);
    --border-right-color: linear-gradient(
      to bottom,
      #540128 0%,
      #540128 89.59%
    );
    --text-shadow-color: #5f0029;
    --top-half-gradient-color: linear-gradient(90deg, #ff008e 0%, #ff1d80 100%);
    --bottom-half-gradient-color: linear-gradient(
      90deg,
      #ff12b3 -7.63%,
      #ff4aa1 108.26%
    );
  }
  &.purple {
    --border-bottom-color: #291038;
    --border-right-color: #291038;
    --text-shadow-color: #101010;
    --top-half-gradient-color: linear-gradient(90deg, #6f01a3 0%, #9e13df 100%);
    --bottom-half-gradient-color: linear-gradient(
      90deg,
      #831db4 -7.63%,
      #a335d9 108.26%
    );
  }
  &.green {
    --border-bottom-color: #056b31;
    --border-right-color: #056b31;
    --text-shadow-color: #101010;
    --top-half-gradient-color: linear-gradient(
      330deg,
      #33ce78 2.3%,
      #03662d 95.33%
    );
    --bottom-half-gradient-color: linear-gradient(
      90deg,
      rgba(var(--c-rgb-white), 0.11),
      rgba(var(--c-rgb-white), 0.11)
    );
  }

  padding: var(--intermediate-unit) var(--triple-unit);
  transition: 0.3s ease transform;
  font-size: var(--fs-24);
  font-family: var(--sorare-font);
  font-weight: var(--t-bold);
  min-width: calc(300px - var(--border-width) - var(--offset-width));
  min-height: calc(7 * var(--unit));
  line-height: 1;
  transform-origin: center;
  transform: translate(var(--offset-width), calc(var(--half-border-width) * -1))
    skew(calc(var(--skew) * -1));
  &::before {
    position: absolute;
    content: '';
    z-index: 0;
    left: 50%;
    bottom: var(--border-width);
    transform: translateX(-50%);
    width: 40%;
    height: 50%;
    border-radius: 50% 50% 0 0;
    filter: blur(10px);
    background: rgba(255, 255, 255, 0.2);
    opacity: 0;
    transition: 0.3s ease opacity;
  }

  &.ticket:hover,
  &.freePlay:hover {
    transform: skew(calc(var(--skew) * -1))
      translate(calc(var(--offset-width) + var(--half-border-width)), 0);
    &::before {
      opacity: 1;
    }
    ${TopHalf}, ${BottomHalf} {
      &::before {
        transform: scaleX(0.5) skewY(var(--skew));
      }
      &::after {
        transform: scaleY(0.5) skewX(var(--skew))
          translateY(calc(var(--half-border-width) * -1));
      }
    }
  }
`;

const Colors = styled.span`
  position: absolute;
  z-index: -1;
  content: '';
  inset: 0 0 0 calc(var(--offset-width) * -1);

  /** background and mask for the top left border */
  background: linear-gradient(
    190deg,
    var(--thin-top-border-color) 50%,
    transparent 100%
  );
  clip-path: polygon(
    0 0,
    calc(100% - var(--offset-width)) 0,
    calc(100% - var(--offset-width)) 50%,
    100% 50%,
    100% 100%,
    var(--offset-width) 100%,
    var(--offset-width) 50%,
    0 50%
  );

  &::before,
  &::after {
    position: absolute;
    inset: 0;
    content: '';
  }
  &::before {
    /** Top half gradient shape */
    clip-path: polygon(
      1px 1px,
      calc(50% - var(--offset-width)) 0,
      calc(100% - var(--offset-width)) 0,
      calc(100% - var(--offset-width)) 50%,
      100% 50%,
      100% 100%,
      calc(var(--offset-width) + 1px) 100%,
      calc(var(--offset-width) + 1px) calc(50% - 1px),
      1px calc(50% - 1px)
    );
    background: var(--top-half-gradient-color);
  }
  &::after {
    /** Bottom half gradient shape */
    clip-path: polygon(
      var(--offset-width) 80%,
      75% 35%,
      74% 50%,
      100% 30%,
      100% 50%,
      100% 100%,
      var(--offset-width) 100%
    );
    background: var(--bottom-half-gradient-color);
  }
`;
const UnSkew = styled.span`
  width: 100%;
  transform: skew(var(--skew));
`;
const TextShadow = styled.span`
  text-shadow:
    0 1px 0 var(--text-shadow-color),
    0 2px 0 var(--text-shadow-color),
    -1px 0px 0 var(--text-shadow-color),
    -1px -1px 0 var(--text-shadow-color),
    -1px 1px 0 var(--text-shadow-color),
    1px 0px 0 var(--text-shadow-color),
    1px -1px 0 var(--text-shadow-color),
    1px 1px 0 var(--text-shadow-color),
    -1px 2px 0 var(--text-shadow-color),
    1px 2px 0 var(--text-shadow-color),
    -1px 3px 0 var(--text-shadow-color),
    1px 3px 0 var(--text-shadow-color);
`;
const Label = styled(TextShadow)`
  display: grid;
  grid-template-areas: '. message icon';
  grid-template-columns: 20px 1fr 20px;
  align-items: center;
  .message {
    grid-area: message;
  }
  .icon {
    grid-area: icon;
  }
`;

const messages = {
  [BrandedPlayButtonStatus.TICKET]: 'Play now',
  [BrandedPlayButtonStatus.NO_TICKET]: 'Play now',
  [BrandedPlayButtonStatus.FREE_PLAY]: 'Play Free Now',
  ...defineMessages({
    [BrandedPlayButtonStatus.EDIT]: {
      id: 'ArenaPage.continue',
      defaultMessage: 'Continue',
    },
  }),
};

type Props = {
  onClick?: () => void;
  status: BrandedPlayButtonStatus;
  width?: number;
  height?: number;
  children?: ReactNode;
} & ComponentProps<typeof ButtonBase>;
export const BrandedPlayButton = ({
  onClick: onClickProp,
  status,
  width,
  height,
  children,
  ...rest
}: Props) => {
  const freePlay = status === BrandedPlayButtonStatus.FREE_PLAY;
  const onClick = () => {
    if (
      [
        BrandedPlayButtonStatus.FREE_PLAY,
        BrandedPlayButtonStatus.TICKET,
        BrandedPlayButtonStatus.GREEN,
        BrandedPlayButtonStatus.EDIT,
        BrandedPlayButtonStatus.NO_TICKET,
      ].includes(status)
    ) {
      onClickProp?.();
    }
  };

  const nonTranslatedText = [
    BrandedPlayButtonStatus.FREE_PLAY,
    BrandedPlayButtonStatus.TICKET,
    BrandedPlayButtonStatus.NO_TICKET,
  ].includes(status);

  let message: string | ReactNode = '';
  if (status in messages) {
    message = nonTranslatedText ? (
      /** @ts-expect-error this can't fail because of the safeguard */
      messages[status]
    ) : (
      /** @ts-expect-error this can't fail because of the safeguard */
      <FormattedMessage {...messages[status]} />
    );
  }

  return (
    <Root className={status} onClick={onClick} {...rest}>
      <TopHalf />
      <BottomHalf />
      <UnSkew>
        {children || (
          <Label>
            <span className="message">{message}</span>
            {![
              BrandedPlayButtonStatus.PURPLE,
              BrandedPlayButtonStatus.EDIT,
              BrandedPlayButtonStatus.NO_TICKET,
            ].includes(status) && (
              <span className="icon">
                <ArenaTicket
                  width={20}
                  count={freePlay ? undefined : 1}
                  free={freePlay}
                />
              </span>
            )}
          </Label>
        )}
      </UnSkew>
      <Colors />
    </Root>
  );
};
